import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, ArrowDown, MonitorSmartphone, Sparkles, BarChart3, Users } from "lucide-react";
import { Reveal, SectionLabel, Stagger, StaggerItem, Highlight } from "@/components/motion";
import CTABanner from "@/components/CTABanner";
import { PRODUCTS } from "@/data/content";

const BrowserMock = ({ product }) => (
  <div className="rounded-3xl bg-white soft-shadow border border-slate-100 overflow-hidden" aria-hidden="true">
    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100 bg-[#F8FAFC]">
      <span className="w-3 h-3 rounded-full bg-[#CAF2F9]" />
      <span className="w-3 h-3 rounded-full bg-[#91E0F1]" />
      <span className="w-3 h-3 rounded-full bg-[#09B2ED]" />
      <div className="ml-3 flex-1 max-w-xs h-6 rounded-lg bg-white border border-slate-200 flex items-center px-3">
        <span className="text-[10px] text-slate-400">app.{product.id}.theaurgelabs.com</span>
      </div>
    </div>
    <div className="p-6 grid grid-cols-3 gap-4">
      <div className="col-span-1 space-y-2.5">
        <div className="h-8 rounded-xl" style={{ background: `${product.accent}30` }} />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-5 rounded-lg bg-slate-100" style={{ width: `${85 - i * 12}%` }} />
        ))}
      </div>
      <div className="col-span-2 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-100 p-3">
              <div className="w-7 h-7 rounded-lg mb-2" style={{ background: i === 0 ? product.accent : "#CAF2F9" }} />
              <div className="h-2 rounded-full bg-slate-100 w-4/5" />
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-100 p-4">
          <div className="flex items-end gap-1.5 h-20">
            {[45, 70, 55, 90, 65, 100, 80, 60, 75, 95].map((h, i) => (
              <div key={i} className="flex-1 rounded-t-md" style={{ height: `${h}%`, background: `linear-gradient(to top, ${product.accent}99, ${product.accent})` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const HIGHLIGHTS = {
  lms: [
    { icon: MonitorSmartphone, title: "Learn anywhere", desc: "Fully responsive learning on any device, online or offline." },
    { icon: Sparkles, title: "AI-assisted authoring", desc: "Generate quizzes and course outlines in minutes." },
    { icon: BarChart3, title: "Deep analytics", desc: "See exactly where learners engage — and where they drop off." },
    { icon: Users, title: "Cohorts & live classes", desc: "Native webinar and classroom sessions built in." },
  ],
  crm: [
    { icon: Users, title: "Pipeline clarity", desc: "Every deal, stage, and owner visible at a glance." },
    { icon: Sparkles, title: "Smart automation", desc: "Follow-ups, reminders and handoffs that run themselves." },
    { icon: BarChart3, title: "Revenue forecasting", desc: "Dashboards that turn pipeline data into predictions." },
    { icon: MonitorSmartphone, title: "Works where you do", desc: "Full mobile experience for teams in the field." },
  ],
  school: [
    { icon: Users, title: "Parent portal", desc: "Real-time updates that keep families in the loop." },
    { icon: BarChart3, title: "Performance insight", desc: "Grades, attendance, and trends in one dashboard." },
    { icon: Sparkles, title: "Automated admin", desc: "Fees, timetables and admissions with zero paperwork." },
    { icon: MonitorSmartphone, title: "Any device", desc: "Teachers and staff work from anywhere, securely." },
  ],
};

const ACTION_STATS = {
  lms: [["50+", "learning metrics tracked"], ["Unlimited", "courses & cohorts"], ["4.9/5", "learner satisfaction"]],
  crm: [["12 hrs", "saved per team, weekly"], ["96%", "adoption within 60 days"], ["1 view", "for pipeline, projects & billing"]],
  school: [["96%", "on-time fee collection"], ["3 weeks", "typical full rollout"], ["1,100+", "parents actively connected"]],
};

export default function Products() {
  return (
    <div data-testid="products-page">
      {/* HERO */}
      <section className="relative pt-36 pb-16 px-5 sm:px-8 overflow-hidden" data-testid="products-hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CAF2F9]/30 to-white" />
        <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-[#91E0F1]/20 blur-3xl" />
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionLabel>Our Products</SectionLabel>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#01193D] leading-[1.1]">
              Beyond services, we build <Highlight delay={0.4}>software we believe in</Highlight>
            </h1>
            <p className="text-[#475569] text-base lg:text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
              The Aurge Labs - LMS, The Aurge Labs - CRM, and The Aurge Labs - School Management System — proprietary SaaS platforms engineered for high performance and real operational scale.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON STRIP */}
      <section className="px-5 sm:px-8 pb-8" data-testid="product-comparison-section">
        <Stagger className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          {PRODUCTS.map((p) => (
            <StaggerItem key={p.id}>
              <Link
                to={p.path}
                data-testid={`compare-card-${p.id}`}
                className="card-lift group flex flex-col h-full bg-white rounded-3xl border border-slate-100 p-8 soft-shadow"
              >
                <span className="inline-flex w-[52px] h-[52px] rounded-2xl items-center justify-center" style={{ background: `linear-gradient(135deg, ${p.accent}25, #FFFFFF)`, border: `1px solid ${p.accent}55` }}>
                  <p.icon size={24} className="text-[#2563EB]" />
                </span>
                <p className="font-mono-brand text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-400 mt-5">{p.tag}</p>
                <h2 className="font-display font-bold text-2xl text-[#01193D] mt-1.5">{p.name}</h2>
                <p className="text-[#475569] text-sm leading-relaxed mt-3 flex-1">{p.tagline}.</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {p.tags.map((tag) => (
                    <span key={tag} className="text-[11px] font-semibold bg-[#CAF2F9] text-[#01193D] px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] mt-6 group-hover:gap-2.5 transition-all">
                  View Full Product Page <ArrowRight size={15} />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* PRODUCT SHOWCASES */}
      {PRODUCTS.map((product, idx) => (
        <section
          key={product.id}
          id={`product-${product.id}`}
          className={`px-5 sm:px-8 py-16 lg:py-24 scroll-mt-24 ${idx % 2 === 1 ? "bg-[#F8FAFC]" : ""}`}
          data-testid={`product-section-${product.id}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <Reveal>
                <div className="flex items-center gap-4">
                  <span className="inline-flex w-14 h-14 rounded-2xl items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, ${product.accent}, #2563EB)` }}>
                    <product.icon size={26} className="text-white" />
                  </span>
                  <div>
                    <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#01193D]">{product.name}</h2>
                    <p className="font-mono-brand text-xs text-slate-400 mt-0.5">by The Aurge Labs</p>
                  </div>
                </div>
                <p className="font-display font-medium text-xl text-[#2563EB] mt-6">"{product.tagline}"</p>
                <p className="text-[#475569] text-base lg:text-lg leading-relaxed mt-4">{product.desc}</p>
                <ul className="grid sm:grid-cols-2 gap-3 mt-7">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#01193D]">
                      <CheckCircle2 size={17} className="text-[#09B2ED] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-4 mt-8">
                  <Link to={product.path} data-testid={`product-page-cta-${product.id}`} className="btn-gradient text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2">
                    Explore {product.shortName} <ArrowRight size={17} />
                  </Link>
                  <Link to="/contact" data-testid={`product-demo-cta-${product.id}`} className="btn-outline-navy font-semibold px-7 py-3.5 rounded-full inline-flex items-center">
                    Request Demo
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={0.15} className="animate-float-slow">
                <BrowserMock product={product} />
              </Reveal>
            </div>

            {/* FEATURE HIGHLIGHT BENTO */}
            <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
              {HIGHLIGHTS[product.id].map((h, i) => (
                <StaggerItem key={h.title}>
                  <div className={`card-lift h-full rounded-3xl p-7 border ${i === 0 ? "bg-[#01193D] border-transparent" : "bg-white border-slate-100 soft-shadow"}`}>
                    <span className={`inline-flex w-11 h-11 rounded-2xl items-center justify-center ${i === 0 ? "bg-white/10" : "bg-[#CAF2F9]"}`}>
                      <h.icon size={20} className={i === 0 ? "text-[#09B2ED]" : "text-[#2563EB]"} />
                    </span>
                    <h3 className={`font-display font-semibold mt-4 ${i === 0 ? "text-white" : "text-[#01193D]"}`}>{h.title}</h3>
                    <p className={`text-sm leading-relaxed mt-2 ${i === 0 ? "text-slate-300" : "text-[#475569]"}`}>{h.desc}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>

            {/* IN ACTION STATS */}
            <Stagger className="grid sm:grid-cols-3 gap-5 mt-8" data-testid={`product-stats-${product.id}`}>
              {ACTION_STATS[product.id].map(([v, l]) => (
                <StaggerItem key={l} className="text-center bg-[#CAF2F9]/50 rounded-3xl py-8 px-4 border border-[#91E0F1]/30">
                  <p className="font-display font-bold text-3xl text-gradient">{v}</p>
                  <p className="text-sm text-[#475569] font-medium mt-1.5">{l}</p>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      ))}

      <CTABanner title="Ready to see it live?" subtitle="Request a personalized demo of EduCults, AgencyOrbits, or the School Management System — on your own use case." cta="Request a Personalized Demo" />
    </div>
  );
}
